import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { css } from '@emotion/css'
import moment from 'moment'
import Swal from 'sweetalert2'
import IPInput from '../../../../../components/IPInput/IPInput'
import IPButton from '../../../../../components/IPButton/IPButton'
import Table from '../../../../../components/Table'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'

import { IPKActivationData, IPKIMEIReportRequest, IPKIMEIReportResponse } from '../models/model'
import { PKIMEIDataReportApi } from '../services/PKIMEIDataReportApi'
import { GetSuccessFullApi } from '../../../CreditCardOperation/Successful/services/getSuccessFullApi'
import { ISuccessFullRequest, ISuccessFullResponse } from '../../../CreditCardOperation/Successful/models/models'
import { formatCurrencyTemlik } from '../../../../../base/utils/dataHelper'

// Küçük, sağa yaslı yönlendirme butonu.
// Bootstrap "btn/btn-outline-*" sınıfları kullanılmıyor: tema CSS'i (assetWeb/css/style.css)
// bu sınıfları eziyor ve hover'da yazıyı beyaza çevirip zemini kaybettiriyor. Tema mavisi (#2250fc)
// burada !important ile sabitlendi ki tema araya girmesin.
const navButtonStyle = css`
  padding: 5px 12px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  line-height: 1.4 !important;
  white-space: nowrap;
  border: 1px solid #2250fc !important;
  border-radius: 4px !important;
  background-color: #2250fc !important;
  color: #ffffff !important;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;

  &:hover {
    background-color: #1a41d6 !important;
    border-color: #1a41d6 !important;
    color: #ffffff !important;
  }
`

// Info List Component
const InfoList = ({ data, compact }: { data: Array<{ label: string; value: any }>; compact?: boolean }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '0.15rem' : '0.75rem', fontSize: compact ? '0.8rem' : undefined }}>
    {data.map((item, idx) => (
      <div
        key={idx}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: compact ? '0.2rem 0.25rem' : '0.5rem',
          borderBottom: '1px solid #e0e0e0',
          gap: compact ? '0.5rem' : '1rem',
        }}
      >
        <span style={{ fontWeight: 500, minWidth: compact ? '110px' : '150px' }}>{item.label}:</span>
        <span
          // Tek satıra sığmayan değerlerin tamamı tooltip'te görünsün.
          title={typeof item.value === 'string' ? item.value : undefined}
          style={{ textAlign: 'right', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {item.value}
        </span>
      </div>
    ))}
  </div>
)

// SAP tarih alanları bazen Date bazen string geliyor; ikisini de tolere ediyoruz.
const formatDate = (value: any): string => {
  if (!value) return '-'
  const date = moment(value)
  return date.isValid() ? date.format('DD.MM.YYYY') : String(value)
}

// PK raporunda ödeme durumu "KONTRAT YAPILDI" ise aktivasyon tamamlanmış demektir.
// Karşılaştırma MyReportsService.GetVodafonePKReport ile aynı: boşluklar kırpılır, harf duyarsız.
const isActivated = (row?: IPKActivationData) =>
  (row?.ODEME_DURUMU || '').trim().toUpperCase() === 'KONTRAT YAPILDI'

function PKIMEIReport(props: BaseComponentWithContextProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [imei, setImei] = useState('')
  const [result, setResult] = useState<IPKIMEIReportResponse | null>(null)
  const [paymentData, setPaymentData] = useState<ISuccessFullResponse[] | null>(null)

  const showError = (title: string, err: any) => {
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      err?.description ||
      err ||
      'Beklenmeyen bir hata oluştu.'
    Swal.fire({ icon: 'error', title, text: String(msg) })
  }

  const onSearch = async () => {
    if (!imei.trim()) {
      Swal.fire({ icon: 'warning', title: 'Eksik bilgi', text: 'IMEI numarası gereklidir.' })
      return
    }

    setLoading(true)
    try {
      props.showLoading()
      const api = new PKIMEIDataReportApi({})
      const payload: IPKIMEIReportRequest = {
        IMEI: imei.trim(),
      }
      const data = await api.getPKIMEIData(payload)
      setResult(data)
    } catch (err) {
      showError('IMEI Sorgusu Başarısız', err)
      setResult(null)
    } finally {
      setLoading(false)
      props.hideLoading()
    }
  }

  const payment = useCallback(async () => {
    if (!result?.OrderNumbers || result.OrderNumbers.length === 0) return
    const paymentApi = new GetSuccessFullApi({})
    try {
      props.showLoading()
      const payload: ISuccessFullRequest = {
        OrderNumberList: result.OrderNumbers,
      }
      const data = await paymentApi.getSuccessFull(payload)
      if (data) {
        setPaymentData(data)
      }
    } catch (err) {
      showError('Ödeme Bilgilerini Getirirken Hata!', err)
    } finally {
      props.hideLoading()
    }
  }, [result?.OrderNumbers])

  useEffect(() => {
    if (result?.OrderNumbers && result.OrderNumbers.length > 0) {
      payment()
    }
    else {
      setPaymentData(null)
    }
  }, [result?.OrderNumbers])

  // DGT Çıkış yolunda backend KDV'yi KDV_TL değil KDV alanına yazıyor; ikisinden dolu olan alınır.
  const kdvTutari = useMemo(
    () => result?.TemlikData ? (result.TemlikData.KDV_TL || result.TemlikData.KDV || 0) : 0,
    [result]
  )

  const temelBilgilerData = useMemo(() => {
    if (!result) return []
    return [
      { label: 'IMEI', value: result.VodafoneData?.IMEI || result.TemlikData?.SERI_NO || '-' },
      { label: 'Fiyat', value: formatCurrencyTemlik(result.VodafoneData?.PRICE || (result.TemlikData ? result.TemlikData.URUN_FIYATI_TL + kdvTutari : 0)) || '-' },
      { label: 'Tip', value: result.Type || '-' },
    ]
  }, [result, kdvTutari])


  const temlikBilgileriData = useMemo(() => {
    if (!result?.TemlikData) return []
    return [
      { label: 'Belge No', value: result.TemlikData.BELGE_NO || '-' },
      { label: 'Evrak No', value: result.TemlikData.EVRAK_NO || '-' },
      { label: 'Fatura Tarihi', value: result.TemlikData.FATURA_TARIHI?.split(' ')[0] || '-' },
      { label: 'Ödeme Şekli', value: result.TemlikData.ODEME_SEKLI || '-' },
      { label: 'Ürün Kodu', value: result.TemlikData.URUN_KODU || '-' },
      { label: 'Ürün Açıklaması', value: result.TemlikData.URUN_ACIKLAMA || '-' },
      { label: 'Seri No', value: result.TemlikData.SERI_NO || '-' },
      { label: 'Ürün Fiyatı (TL)', value: formatCurrencyTemlik(result.TemlikData.URUN_FIYATI_TL) || '-' },
      { label: 'KDV (TL)', value: formatCurrencyTemlik(kdvTutari) || '-' },
      { label: 'Belge PB', value: result.TemlikData.BELGE_PB || '-' },
    ]
  }, [result, kdvTutari])

  // Temel bilgiler + temlik/IMEI bilgileri tek kutucukta gösteriliyor; temlik alanlarının
  // tamamı korunuyor.
  const imeiBilgileriData = useMemo(
    () => [...temelBilgilerData, ...temlikBilgileriData],
    [temelBilgilerData, temlikBilgileriData]
  )


  // Aktivasyon (peşine kontrat) kayıtları - en yeni aktivasyon tarihi başa alınıyor.
  const activationData = useMemo<IPKActivationData[]>(() => {
    if (!result?.ActData || result.ActData.length === 0) return []
    return [...result.ActData].sort((a, b) => {
      const aDate = moment(a.AKTIVASYON_TARIHI)
      const bDate = moment(b.AKTIVASYON_TARIHI)
      if (!aDate.isValid()) return 1
      if (!bDate.isValid()) return -1
      return bDate.valueOf() - aDate.valueOf()
    })
  }, [result?.ActData])

  const lastActivation = activationData[0]

  const aktivasyonBilgileriData = useMemo(() => {
    if (!lastActivation) return []
    return [
      { label: 'Aktivasyon Tarihi', value: formatDate(lastActivation.AKTIVASYON_TARIHI) },
      { label: 'Ödeme Tarihi', value: formatDate(lastActivation.ODEME_TARIHI) },
      { label: 'Tutar', value: formatCurrencyTemlik(lastActivation.TUTAR) },
      { label: 'VDF Bayi Kodu', value: lastActivation.VDF_BAYI_KODU || '-' },
      // Marka, model ve ürün açıklaması tek satırda.
      {
        label: 'Marka / Model / Ürün',
        value:
          [lastActivation.MARKA, lastActivation.MODEL, lastActivation.URUN_ACIKLAMASI]
            .filter(x => x && String(x).trim())
            .join(' · ') || '-',
      },
    ]
  }, [lastActivation])

  const odemeDataColumns = useMemo(() => [
    { Header: 'Durum', accessor: (row) => row.Cancelled ? 'İptal Edildi' : 'Geçerli' },
    { Header: 'Sipariş No', accessor: 'OrderNumber' },
    { Header: 'Makbuz No', accessor: 'SapTakipNO' },
    { Header: 'Bayi Kodu', accessor: (row: any) => row.DealaerCode?.replace("0000", "") },
    { Header: 'İşlem Zamanı', accessor: (row: any) => new Date(row.ProcessTime).toLocaleString('tr-TR') },
    { Header: 'Banka', accessor: 'Bank' },
    { Header: 'Kart Adı', accessor: 'CardName' },
    { Header: 'Bayi Adı', accessor: 'DealerName' },
    { Header: 'Taksit', accessor: (row: any) => (row.PayInstallment !== "0" && row.PayInstallment !== "1") ? `${row.PayInstallment} taksit` : 'Tek Çekim' },
    { Header: 'Tutar', accessor: 'RealPrice' },
    { Header: 'Para Birimi', accessor: 'Currency' },
    {
      Header: 'Komisyon', accessor: (row: any) => (row.ComissionType === 3 || row.IMEI != null)
        ? 'Komisyonsuz'
        : row.ComissionType === 2
          ? 'Hariç'
          : row.ComissionType === 1
            ? 'Dahil'
            : '',
    },
  ], [])

  return (
    <div className="">
      <div className="content col-lg-12 m-auto">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <span className="h4 mb-0">Vodafone PK IMEI Sorgulama</span>
            <button
              type="button"
              className={navButtonStyle}
              onClick={() => navigate('/admin/HomeAndSiteOperation/PKActivationExceptions')}
            >
              Aktivasyon Kontrolü Muafiyetleri
            </button>
          </div>

          <div className="card-body">
            <div className="row g-3 mb-4">
              <div className="col-md-8">
                <label className="form-label">IMEI Numarası</label>
                <IPInput
                  value={imei}
                  onChange={(e: any) => setImei(e?.target?.value ?? e)}
                  type="text"
                  placeholder="IMEI numarası girin..."
                />
              </div>

              <div className="col-md-4 d-flex align-items-end">
                <IPButton
                  text={loading ? 'Sorgulanıyor...' : 'Sorgula'}
                  disabled={loading || !imei.trim()}
                  onClick={onSearch}
                />
              </div>
            </div>

            {result && (
              <div>
                <h5 className="mb-4">Sorgu Sonuçları</h5>

                {/* Tek satırda üç kutucuk: Özet - Aktivasyon - IMEI bilgileri */}
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <div className="card border-primary">
                      <div className="card-body py-2 px-3">
                        <h6 className="card-title mb-2" style={{ fontSize: '0.85rem' }}>
                          Özet Bilgiler
                        </h6>
                        <div className="row">
                          <div className="col-6">
                            <small className="text-muted d-block">Toplam Kullanılan (TL)</small>
                            <span className="fw-bold">{formatCurrencyTemlik(result.TotalUsed) || '-'}</span>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">Toplam Ödenebilir (TL)</small>
                            <span className="fw-bold">{formatCurrencyTemlik(result.PriceData?.TotalPrice) || '-'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div
                      className={`card ${lastActivation ? (isActivated(lastActivation) ? 'border-success' : 'border-warning') : ''
                        }`}
                    >
                      <div className="card-body py-2 px-3">
                        <h6 className="card-title mb-2 d-flex justify-content-between align-items-center" style={{ fontSize: '0.85rem' }}>
                          <span>Aktivasyon Bilgileri</span>
                          {activationData.length > 1 && (
                            <small className="text-muted fw-normal">{activationData.length} kayıt</small>
                          )}
                        </h6>
                        {lastActivation ? (
                          <>
                            <div className="mb-1">
                              <small className="text-muted d-block">Durum</small>
                              <span
                                className="fw-bold"
                                style={{ color: isActivated(lastActivation) ? 'green' : 'red' }}
                              >
                                {isActivated(lastActivation) ? 'AKTİVASYON YAPILDI' : 'AKTİVASYON BEKLENİYOR'}
                              </span>
                            </div>
                            <InfoList data={aktivasyonBilgileriData} compact />
                          </>
                        ) : (
                          <small className="text-muted">Bu IMEI için aktivasyon kaydı bulunamadı.</small>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="card">
                      <div className="card-body py-2 px-3">
                        <h6 className="card-title mb-2" style={{ fontSize: '0.85rem' }}>
                          IMEI Bilgileri
                        </h6>
                        <InfoList data={imeiBilgileriData} compact />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ödeme Bilgileri Tablosu */}
                {paymentData && paymentData.length > 0 && (
                  <div className="row g-3 mb-4">
                    <div className="col-12">
                      <h6 className="mb-3">Ödeme Bilgileri</h6>
                      <Table columns={odemeDataColumns} data={paymentData} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {!result && !loading && (
              <div className="text-center py-4 text-muted">
                <p>IMEI numarası girerek sorgu yapın.</p>
              </div>
            )}

            {loading && (
              <div className="text-center py-4">
                <p>Sorgu yapılıyor...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(PKIMEIReport)
